import { registerView, View, ViewComponent } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { useCallback } from "react";
import { Form } from "react-bootstrap";
import ChargeTypeViewModel from "viewModels/eshop/order/steps/chargeTypeViewModel";
import ValiditySelect from "../components/validitySelect";
import AlertWithInfoIcon from "views/common/alertWithInfoIcon/alertWithInfoIcon";
import { DEFAULT_FORMAT, formatDate } from "@edaz/edazkit/DatePicker";
import { StepsNavigationButtons } from "../components/stepsNavigationButtons";
import Tooltip from "controls/tooltip";
import { getTimeZoneLabel } from "utils/dateUtils";
import { isMobile } from "react-device-detect";

const ChargeTypeView: ViewComponent<ChargeTypeViewModel> = observer(({ vm }) => {
  const dateRegex = /%DATE%/g;
  const tg = (value: string) => vm.localizationService.translateGeneral(`eshop.order.${value}`);
  const translate = (value: string) => vm.localizationService.translateGeneral(`eshop.order.charge.${value}`);
  const translateOrderItem = (value: string) => vm.localizationService.translateAttribute("orderItem", value);
  const translateOverlappingCharge = (validSince: Date, validUntil: Date, isDifferentTimeZone?: boolean) => {
    const renderDateWithTimeZone = (date: Date) => {
      const formatted = vm.localizationService.formatDateTime(date);
      return !isDifferentTimeZone ? (
        <span>{formatted}</span>
      ) : (
        <>
          <span>{formatted}</span>
          <Tooltip
            id="cet-tooltip"
            placement={isMobile ? "bottom" : "right"}
            tooltip={vm.localizationService.translateGeneral("timezoneTooltip")}
            trigger={["hover", "focus"]}>
            <span style={{ marginLeft: 4, textDecoration: "underline", textDecorationStyle: "dotted" }}>
              {getTimeZoneLabel(date)}
            </span>
          </Tooltip>
        </>
      );
    };

    return (
      <span>
        {vm.localizationService.translateGeneral("eshop.order.charge.overlappingChargeStart")}
        {renderDateWithTimeZone(validSince)}
        {vm.localizationService.translateGeneral("eshop.order.charge.overlappingChargeEnd")}
        {renderDateWithTimeZone(validUntil)}
        {"."}
      </span>
    );
  };

  const formRef = useCallback(node => {
    if (node !== null) {
      vm.formElement = node;
    }
  }, []);

  return (
    <>
      <div>
        <Form ref={formRef}>
          <View vm={vm.chargeSelectViewModel} />
        </Form>
        <Form.Row>
          <ValiditySelect vm={vm.validSinceSelectViewModel} />
        </Form.Row>
        {vm.isSingleCharTypeToday && (
          <AlertWithInfoIcon variant="warning-dark">
            <span>{translate("isSingleCharTypeToday")}</span>
          </AlertWithInfoIcon>
        )}
        {vm.showExemptionOverlap && (
          <AlertWithInfoIcon variant="warning">
            <span>{translate("exemptionOverlap")}</span>
          </AlertWithInfoIcon>
        )}
        {vm.proposedValidityFlag && (
          <AlertWithInfoIcon variant="warning-dark">
            <span>{translateOrderItem("systemChangedFieldAutomatically")}</span>
          </AlertWithInfoIcon>
        )}
        {vm.isDifferentTimeZone && (
          <AlertWithInfoIcon variant="warning">
            <span>{translate("differentTimeZone")}</span>
          </AlertWithInfoIcon>
        )}
        {vm.showChargeOverlap && vm.overlappingCharges?.length && vm.overlappingCharges?.length > 0 && (
          <AlertWithInfoIcon variant="warning">
            {vm.overlappingCharges.map((charge, index) => {
              return (
                <p key={index}>{translateOverlappingCharge(charge.validSince, charge.validUntil, vm.isDifferentTimeZone)}</p>
              );
            })}
          </AlertWithInfoIcon>
        )}
      </div>
      <StepsNavigationButtons
        tg={tg}
        continueDisabled={vm.checkingProposedValidity}
        onBack={() => vm.onBack()}
        onContinue={() => vm.onContinue()}
      />
    </>
  );
});

registerView(ChargeTypeView, ChargeTypeViewModel);
